/**
 * Secure localStorage utility for storing sensitive data
 * Data is base64 encoded before storing and decoded when retrieving
 */

import { isBrowser } from "./functions";

/**
 * Encodes a string to base64
 */
const encodeBase64 = (data: string): string => {
  if (!isBrowser()) return data;
  try {
    return btoa(unescape(encodeURIComponent(data)));
  } catch (error) {
    console.error("Error encoding to base64:", error);
    return data;
  }
};

/**
 * Decodes a base64 string
 */
const decodeBase64 = (encoded: string): string => {
  if (!isBrowser()) return encoded;
  try {
    return decodeURIComponent(escape(atob(encoded)));
  } catch (error) {
    console.error("Error decoding from base64:", error);
    return encoded;
  }
};

/**
 * Stores sensitive data in localStorage with base64 encoding
 * @param key - The localStorage key
 * @param value - The value to store (will be JSON stringified and base64 encoded)
 */
export const setSecureItem = (key: string, value: any): void => {
  if (!isBrowser()) return;

  try {
    const jsonString = JSON.stringify(value);
    const encoded = encodeBase64(jsonString);
    localStorage.setItem(key, encoded);
  } catch (error) {
    console.error(`Error storing secure item ${key}:`, error);
  }
};

/**
 * Retrieves and decrypts sensitive data from localStorage
 * Handles both base64-encoded (new format) and plain JSON (old format) for backward compatibility
 * @param key - The localStorage key
 * @returns The decrypted value or null if not found
 */
export const getSecureItem = <T = any>(key: string): T | null => {
  if (!isBrowser()) return null;

  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    // Try to decode as base64 first (new format)
    try {
      const decoded = decodeBase64(stored);
      return JSON.parse(decoded) as T;
    } catch (base64Error) {
      // If base64 decoding fails, try parsing as plain JSON (old format)
      // This provides backward compatibility during migration
      try {
        const parsed = JSON.parse(stored) as T;
        // If successful, migrate to new format
        setSecureItem(key, parsed);
        return parsed;
      } catch (jsonError) {
        // If both fail, the data is corrupted
        console.error(`Error retrieving secure item ${key}:`, jsonError);
        return null;
      }
    }
  } catch (error) {
    console.error(`Error retrieving secure item ${key}:`, error);
    return null;
  }
};

/**
 * Removes a secure item from localStorage
 * @param key - The localStorage key
 */
export const removeSecureItem = (key: string): void => {
  if (!isBrowser()) return;
  localStorage.removeItem(key);
};

